import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { healthService, HealthProgramGroupCalendarDto, setCurrentUser } from '../../services/health';
import { useAuth } from 'react-oidc-context';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import PageHeader from '../../components/UI/PageHeader';
import FormInput from '../../components/Forms/FormInput';
import FormSelect from '../../components/Forms/FormSelect';
import { HealthProgramGroupDto } from '../../services/health/types';

const ProgramGroupCalendarCreate: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const auth = useAuth();

  // Update the current user for the health service
  useEffect(() => {
    setCurrentUser(auth.user || null);
  }, [auth.user]);
  
  const [formData, setFormData] = useState<HealthProgramGroupCalendarDto>({
    name: '',
    description: '',
    timezone: 'AMERICA_SAO_PAULO',
    starts_in: '',
    ends_in: ''
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [programGroup, setProgramGroup] = useState<HealthProgramGroupDto | null>(null);

  useEffect(() => {
    const fetchProgramGroup = async () => {
      if (!id) return;
      
      try {
        const programGroupData = await healthService.getProgramGroupById(id);
        setProgramGroup(programGroupData);
      } catch (error) {
        console.error('Error fetching program group:', error);
      }
    };
    
    fetchProgramGroup();
  }, [id]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error for this field when user changes it
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.starts_in) {
      newErrors.starts_in = 'Start date is required';
    }

    if (!formData.ends_in) {
      newErrors.ends_in = 'End date is required';
    } else if (formData.starts_in && new Date(formData.starts_in) >= new Date(formData.ends_in)) {
      newErrors.ends_in = 'End date must be after start date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate() || !id) {
      return;
    }

    setLoading(true);
    try {
      await healthService.createProgramGroupCalendar(id, formData);
      navigate(`/health/program-groups/${id}?tab=calendars`);
    } catch (error) {
      console.error('Error creating calendar:', error);
      // Could add error handling/notification here
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(`/health/program-groups/${id}?tab=calendars`);
  };

  return (
    <div>
      <PageHeader
        title="Create Calendar"
        breadcrumbs={[
          { label: "Dashboard", to: "/" },
          { label: "Health" },
          { label: "Program Groups", to: "/health/program-groups" },
          { label: programGroup?.name || 'Program Group', to: `/health/program-groups/${id}` },
          { label: "Calendars", to: `/health/program-groups/${id}/calendars` },
          { label: "Create" }
        ]}
        actions={
          <Button
            variant="secondary"
            onClick={() => navigate(`/health/program-groups/${id}`)}
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Back to Program Group
          </Button>
        }
      />

      <Card className="mt-6">
        <form onSubmit={handleSubmit} className="p-4 space-y-6">
          <FormInput
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            required
            error={errors.name}
          />
          
          <FormInput
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            required
            error={errors.description}
          />

          <FormSelect
            label="Timezone"
            name="timezone"
            value={formData.timezone}
            onChange={handleInputChange}
            required
            options={[
              { value: "AMERICA_SAO_PAULO", label: "America/Sao Paulo" },
              { value: "UTC", label: "UTC" }
            ]}
          />

          <FormInput
            label="Start Date"
            name="starts_in"
            type="datetime-local"
            value={formData.starts_in}
            onChange={handleInputChange}
            required
            error={errors.starts_in}
          />

          <FormInput
            label="End Date"
            name="ends_in"
            type="datetime-local"
            value={formData.ends_in}
            onChange={handleInputChange}
            required
            error={errors.ends_in}
          />

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Calendar'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default ProgramGroupCalendarCreate;